import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  styled,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import GameLogosForm from "./GameLogosForm";
import PopoverPicker from "../../qrCodeManagement/PopoverColorPicker";
import { useSelector } from "react-redux";
import { hexToRgba, rgbaToHex, useDebounce } from "../../../helpers";
import {
  addElement,
  updateElement,
  updateMultiSelection,
} from "../../../reducers/configurator/configuratorSlice";
import { useDispatch } from "react-redux";
import { OpenWith, Visibility, VisibilityOff } from "@mui/icons-material";
import PickleballCustom from "../configuratorForms/PickleballCustom";
import BasketballCustom from "../configuratorForms/BasketballCustom";
import FootballCustom from "../configuratorForms/FootballCustom";

const PREFIX = "gameDetails";

const classes = {
  baseContainer: `${PREFIX}-baseContainer`,
  contentContainer: `${PREFIX}-contentContainer`,
  popoverContentContainer: `${PREFIX}-popoverContentContainer`,
  popoverContainer: `${PREFIX}-popoverContainer`,
};

const GameDetailsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.baseContainer}`]: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      padding: "0 1rem",
      marginBottom: "1rem",
    },
    [`& .${classes.contentContainer}`]: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      justifyContent: "flex-start",
      marginBottom: "2rem",
    },
    [`& .${classes.popoverContentContainer}`]: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      justifyContent: "space-between",
      paddingBottom: "2rem",
      marginBottom: "1rem",
    },
    [`& .${classes.popoverContainer}`]: {
      display: "flex",
      justifyContent: "center",
    },
  };
});

const BLUE_COLOR = {
  r: 0,
  g: 33,
  b: 231,
  a: 1,
};
const RED_COLOR = {
  r: 255,
  g: 0,
  b: 0,
  a: 1,
};

const GameDetails = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentTile = useSelector((state) => state.configurator.selectedTile);
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const myTile = useSelector((state) =>
    currentTile
      ? state.configurator.grid[parentLayout][currentTile.row][
          currentTile.column
        ]
      : null
  );

  const [hexaInputs, setHexaInputs] = useState({
    left: myTile.middle.leftSocleColor
      ? rgbaToHex(
          `rgba(${myTile.middle.leftSocleColor.r}, ${myTile.middle.leftSocleColor.g}, ${myTile.middle.leftSocleColor.b}, ${myTile.middle.leftSocleColor.a})`
        )
      : "#0021e7",
    right: myTile.middle.rightSocleColor
      ? rgbaToHex(
          `rgba(${myTile.middle.rightSocleColor.r}, ${myTile.middle.rightSocleColor.g}, ${myTile.middle.rightSocleColor.b}, ${myTile.middle.rightSocleColor.a})`
        )
      : "#ff0000a",
  });
  const [inputsValues, setInputsValues] = useState({
    left: myTile.middle.leftSocleColor || BLUE_COLOR,
    right: myTile.middle.rightSocleColor || RED_COLOR,
  });

  const debouncedLeftHexa = useDebounce(hexaInputs.left, 500);
  const debouncedRightHexa = useDebounce(hexaInputs.right, 500);
  const debouncedLeftInput = useDebounce(inputsValues.left, 500);
  const debouncedRightInput = useDebounce(inputsValues.right, 500);

  useEffect(() => {
    if (myTile.middle.leftSocleColor && myTile.middle.rightSocleColor) {
      const myLeftColor = myTile.middle.leftSocleColor;
      const myRightColor = myTile.middle.rightSocleColor;
      setInputsValues({
        left: myLeftColor,
        right: myRightColor,
      });
      setHexaInputs({
        left: rgbaToHex(
          `rgba(${myLeftColor.r}, ${myLeftColor.g}, ${myLeftColor.b}, ${myLeftColor.a})`
        ),
        right: rgbaToHex(
          `rgba(${myRightColor.r}, ${myRightColor.g}, ${myRightColor.b}, ${myRightColor.a})`
        ),
      });
    } else {
      dispatch(
        addElement({
          layer: "middle",
          data: {
            ...myTile.middle,
            leftSocleColor: BLUE_COLOR,
            rightSocleColor: RED_COLOR,
          },
          tile: { row: myTile.row, column: myTile.column },
        })
      );
    }
  }, []);

  const saveValues = (isHexa = true) => {
    let leftColor, rightColor;
    if (isHexa) {
      leftColor = hexToRgba(hexaInputs.left);
      rightColor = hexToRgba(hexaInputs.right);
    } else {
      leftColor = inputsValues.left;
      rightColor = inputsValues.right;
    }
    dispatch(
      addElement({
        layer: "middle",
        data: {
          ...myTile.middle,
          leftSocleColor: { ...leftColor },
          rightSocleColor: { ...rightColor },
        },
        tile: { row: myTile.row, column: myTile.column },
      })
    );
  };

  useEffect(() => {
    if (debouncedLeftHexa) {
      setInputsValues({
        ...inputsValues,
        left: hexToRgba(hexaInputs.left),
      });
      saveValues();
    }
  }, [debouncedLeftHexa]);

  useEffect(() => {
    if (debouncedRightHexa) {
      setInputsValues({
        ...inputsValues,
        right: hexToRgba(hexaInputs.right),
      });
      saveValues();
    }
  }, [debouncedRightHexa]);

  useEffect(() => {
    if (debouncedLeftInput) {
      setHexaInputs({
        ...hexaInputs,
        left: rgbaToHex(
          `rgba(${inputsValues.left.r}, ${inputsValues.left.g}, ${inputsValues.left.b}, ${inputsValues.left.a})`
        ),
      });
      saveValues(false);
    }
  }, [debouncedLeftInput]);

  useEffect(() => {
    if (debouncedRightInput) {
      setHexaInputs({
        ...hexaInputs,
        right: rgbaToHex(
          `rgba(${inputsValues.right.r}, ${inputsValues.right.g}, ${inputsValues.right.b}, ${inputsValues.right.a})`
        ),
      });
      saveValues(false);
    }
  }, [debouncedRightInput]);

  return (
    <GameDetailsContainer style={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <IconButton
          color="primary"
          onClick={() => {
            const tmp = { ...myTile.middle };
            if (tmp.endTile) delete tmp.endTile;
            dispatch(
              updateMultiSelection({
                data: tmp,
                move: true,
                active: true,
                tile: currentTile,
                oldLayer: "middle",
                layer: "middle",
              })
            );
          }}
        >
          <OpenWith fontSize="medium" />
          <Typography
            variant="body2"
            sx={{ marginLeft: "0.5rem", color: "#D3Af5F" }}
          >
            {t("myExpariences.form.move")}
          </Typography>
        </IconButton>
        <IconButton
          color="primary"
          aria-label="element visibility"
          component="span"
          onClick={() => {
            dispatch(
              updateElement({
                layer: "middle",
                data: {
                  ...myTile.middle,
                  visibleRoom: !myTile.middle.hasOwnProperty("visibleRoom")
                    ? false
                    : !myTile.middle.visibleRoom,
                },
                tile: myTile.middle.startTile
                  ? myTile.middle.startTile
                  : currentTile,
                tileEnd: myTile.middle.endTile ? myTile.middle.endTile : null,
              })
            );
          }}
        >
          {!myTile.middle.hasOwnProperty("visibleRoom") ||
          myTile.middle.visibleRoom ? (
            <Visibility fontSize="medium" />
          ) : (
            <VisibilityOff fontSize="medium" />
          )}
        </IconButton>
      </Box>
      {myTile.middle.type.includes("pickleball") ? (
        <PickleballCustom />
      ) : myTile.middle.type.includes("basket") ? (
        <BasketballCustom />
      ) : myTile.middle.type.includes("football") ? (
        <FootballCustom />
      ) : null}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          margin: "1.5rem 0 1.5rem 0",
        }}
      >
        <FormControlLabel
          label={t("myExpariences.form.fieldDisplacement")}
          control={
            <Checkbox
              checked={myTile.middle.canFieldMove}
              onChange={(e) => {
                dispatch(
                  updateElement({
                    layer: "middle",
                    data: {
                      ...myTile.middle,
                      canFieldMove: e.target.checked,
                    },
                    tile: {
                      row: currentTile.row,
                      column: currentTile.column,
                    },
                  })
                );
              }}
            />
          }
        />
      </Box>
      <div className={classes.baseContainer}>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.basketLeaderboardLogo")}
          </Typography>
          <GameLogosForm logoKey={"leaderboardLogo"} />
        </div>
        <div className={classes.contentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.basketSocleLogo")}
          </Typography>
          <GameLogosForm logoKey={"socleLogo"} />
        </div>
      </div>
      <Box className={classes.baseContainer}>
        <div className={classes.popoverContentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.basketSocleColorLeft")}
          </Typography>
          <div className={classes.popoverContainer}>
            <PopoverPicker
              small
              width={50}
              color={inputsValues.left}
              hexColor={hexaInputs.left}
              onChangeHex={(hex) => {
                setHexaInputs({ ...hexaInputs, left: hex });
              }}
              onChange={(value) =>
                setInputsValues({ ...inputsValues, left: value })
              }
              mode="rgba"
            />
          </div>
        </div>
        <div className={classes.popoverContentContainer}>
          <Typography
            variant="body1"
            sx={{ marginBottom: "1rem", textAlign: "center" }}
          >
            {t("myExpariences.form.basketSocleColorRight")}
          </Typography>
          <div className={classes.popoverContainer}>
            <PopoverPicker
              small
              width={50}
              color={inputsValues.right}
              hexColor={hexaInputs.right}
              onChangeHex={(hex) => {
                setHexaInputs({ ...hexaInputs, right: hex });
              }}
              onChange={(value) =>
                setInputsValues({ ...inputsValues, right: value })
              }
              mode="rgba"
            />
          </div>
        </div>
      </Box>
    </GameDetailsContainer>
  );
};

export default GameDetails;
